<template>
  <div class="form-datetime" :class="{ 'form-datetime--dark': darkEnabled }">
    <div class="form-datetime__field">
      <Datetime
        :class="{ 'has-error': hasErros }"
        type="datetime"
        :disabled="disabled"
        v-model="model"
        :format="format"
        :placeholder="label"
      />
      <button
        v-if="clearable && hasValue"
        type="button"
        class="form-datetime__clear"
        :disabled="disabled"
        aria-label="Limpar data e hora"
        @click="onClear"
      >
        <v-icon small>fas fa-times</v-icon>
      </button>
    </div>
    <div v-if="hasErros" class="error-info">
      <span>{{ errors[0] }}</span>
    </div>
  </div>
</template>

<script>
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";
import { Settings } from "luxon";
Settings.defaultLocale = "pt";

import { mapGetters } from "vuex";
import { mask } from "vue-the-mask";

import * as moment from "moment";
export default {
  name: "FormDatetimePicker",
  components: { Datetime },
  props: {
    value: {
      type: String,
      default: "",
      required: false
    },
    label: {
      type: String,
      default: "",
      required: false
    },
    hint: {
      type: String,
      default: "",
      required: false
    },
    format: {
      type: String,
      default: "dd/MM/yyyy HH:mm:ss",
      required: false
    },
    formatter: {
      type: String,
      default: "DD/MM/YYYY HH:mm:ss",
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    errorMessage: {
      type: [Object],
      default: () => ({}),
      required: false
    },
    validation: {
      type: Object,
      default: () => null,
      required: false
    },
    clearable: {
      type: Boolean,
      default: true,
      required: false
    }
  },
  directives: {
    mask
  },
  data: () => ({
    visibled: false,
    model: "",
    touched: false,
    menu: false,
    dateFormated: ""
  }),
  created() {
    this.model = this.normalizeModelValue(this.value);
  },
  watch: {
    value: {
      deep: true,
      handler(newVal, oldVal) {
        const normalized = this.normalizeModelValue(newVal);
        if (newVal != oldVal && normalized != this.model) {
          this.model = normalized;
        }
      }
    },
    validation: {
      deep: true,
      handler(newVal) {
        this.touched = newVal.$dirty;
      }
    },
    model: {
      handler(newVal) {
        const normalized = this.normalizeEmitValue(newVal);
        this.$emit("change", normalized);
        this.$emit("input", normalized);
      }
    }
  },
  computed: {
    ...mapGetters(["darkEnabled"]),
    hasValue() {
      return !!(this.model && String(this.model).trim());
    },
    hasErros() {
      return this.validation && this.validation.$dirty && this.errors.length;
    },
    errors() {
      if (this.validation && (this.validation.$dirty || this.touched)) {
        let errorsMess = [];
        if (!this.validation.required) {
          errorsMess.push(this.errorMessage["required"] || "Campo obrigatório");
        }
        return errorsMess;
      }
      return [];
    },
    placeholder() {
      return this.model ? this.formatDate(this.model) : this.label;
    }
  },
  methods: {
    normalizeModelValue(value) {
      return value && String(value).trim() ? value : null;
    },
    normalizeEmitValue(value) {
      return value && String(value).trim() ? value : null;
    },
    onClear() {
      this.model = null;
    },
    onBlur() {
      this.$emit("blur", this.model);
      this.checkValidation();
    },
    checkValidation() {
      if (this.validation) {
        this.validation.$touch();
        this.touched = true;
      }
    },
    formatDate(date) {
      return date ? moment(date).format(this.formatter) : "";
    }
  }
};
</script>

<style lang="scss">
.form-datetime {
  margin-bottom: 20px;
}
.form-datetime__field {
  position: relative;
  display: flex;
  align-items: center;
}
.form-datetime__clear {
  position: absolute;
  right: 12px;
  z-index: 1;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: rgba(0, 0, 0, 0.54);
}
.form-datetime--dark .form-datetime__clear {
  color: rgba(255, 255, 255, 0.7);
}
.form-datetime__field .vdatetime {
  flex: 1;
}
.form-datetime__field .vdatetime-input {
  padding-right: 36px;
}
.vdatetime {
  border: solid 1px #ccc;
  padding: 8px 24px;
  border-radius: 26px;
  width: 100%;
  .vdatetime-input {
    width: 100%;
  }
}
.vdatetime.has-error {
  border: solid 2px red;
}
.error-info {
  color: #ff5252 !important;
  font-size: 12px;
  padding: 5px 12px;
}

/* Popup do vue-datetime fica dentro do componente — tema escuro alinhado ao backoffice */
.form-datetime--dark {
  .vdatetime {
    border-color: rgba(255, 255, 255, 0.24);
    background: var(--dark-surface-input, #1a1a1a);
  }

  .vdatetime-input {
    color: rgba(255, 255, 255, 0.87);
  }

  .vdatetime-input::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  .vdatetime-popup {
    background: var(--dark-surface, #1d1d1d) !important;
    color: rgba(255, 255, 255, 0.87) !important;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
  }

  .vdatetime-popup__header {
    background: #2a2a2a !important;
    color: #fff !important;
  }

  .vdatetime-popup__actions__button {
    color: var(--primary-color, #64b5f6) !important;
  }

  .vdatetime-popup__actions__button:hover {
    color: rgba(255, 255, 255, 0.95) !important;
  }

  .vdatetime-calendar__current--month {
    color: rgba(255, 255, 255, 0.87) !important;
  }

  .vdatetime-calendar__month__weekday,
  .vdatetime-calendar__month__day {
    color: rgba(255, 255, 255, 0.87) !important;
  }

  .vdatetime-calendar__month__day:hover > span > span {
    background: rgba(255, 255, 255, 0.12) !important;
  }

  .vdatetime-calendar__month__day--selected > span > span,
  .vdatetime-calendar__month__day--selected:hover > span > span {
    background: var(--primary-color, #64b5f6) !important;
    color: #fff !important;
  }

  .vdatetime-calendar__navigation--previous svg path,
  .vdatetime-calendar__navigation--next svg path {
    stroke: rgba(255, 255, 255, 0.7) !important;
  }

  .vdatetime-calendar__navigation--previous:hover svg path,
  .vdatetime-calendar__navigation--next:hover svg path {
    stroke: #fff !important;
  }

  .vdatetime-time-picker__item {
    color: rgba(255, 255, 255, 0.87) !important;
  }

  .vdatetime-time-picker__item--selected {
    color: var(--primary-color, #64b5f6) !important;
  }

  .vdatetime-time-picker__list::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.25) !important;
  }

  .vdatetime-time-picker__list::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.06) !important;
  }

  .vdatetime-year-picker__item {
    color: rgba(255, 255, 255, 0.87) !important;
  }

  .vdatetime-year-picker__item--selected {
    color: var(--primary-color, #64b5f6) !important;
  }

  .vdatetime-year-picker__list::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.25) !important;
  }

  .vdatetime-year-picker__list::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.06) !important;
  }

  .vdatetime-month-picker__item {
    color: rgba(255, 255, 255, 0.87) !important;
  }

  .vdatetime-month-picker__item--selected {
    color: var(--primary-color, #64b5f6) !important;
  }

  .vdatetime-month-picker__list::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.25) !important;
  }

  .vdatetime-month-picker__list::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.06) !important;
  }
}
</style>
